import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Select, MenuItem, FormControl, Stack
} from '@mui/material';
import * as api from './salesChannelsApi';
import type { SalesChannel, Country, TenantConfig } from './salesChannelsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    salesChannelId: number | null;
}

export function SalesChannelView({ salesChannelId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<SalesChannel>();

    const [countries, setCountries] = useState<Country[]>([]);
    const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);

    const isNew = salesChannelId == null;

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            const [countriesData, configData] = await Promise.all([
                api.getCountries(),
                api.getTenantConfig(),
            ]);

            setCountries(countriesData);
            setTenantConfig(configData);

            if (isNew) {
                detail.setItem({
                    id: 0,
                    salesChannelCode: '',
                    salesChannelDescription: '',
                    country: null,
                    pricelists: [],
                });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getSalesChannel(salesChannelId as number);
                if (!data) throw new Error(`SalesChannel ${salesChannelId} returned empty response`);
                data.pricelists = data.pricelists ?? [];
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [salesChannelId, isNew]);

    useEffect(() => { loadData(); }, [loadData]);

    const salesChannel = detail.item;

    // Validation
    const isSalesChannelCodeValid = (): boolean => {
        if (!tenantConfig || !salesChannel?.salesChannelCode) return false;
        return !tenantConfig.assignedSalesChannelCodes.includes(salesChannel.salesChannelCode);
    };

    const isAllValid = (): boolean => {
        if (!detail.isNew) return true;
        return isSalesChannelCodeValid();
    };

    const save = async () => {
        if (!salesChannel) return;
        await detail.withSaving(async () => {
            if (detail.isNew) {
                const response = await api.createSalesChannel(salesChannel);
                detail.setItem(response);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            } else {
                const response = await api.updateSalesChannel(salesChannel);
                detail.setItem(response);
                detail.setIsEditing(false);
            }
        });
    };

    return (
        <DetailViewShell<SalesChannel>
            featureKey="devFeature_reactSalesChannelView"
            title={`${salesChannel?.salesChannelCode ?? ''} ${salesChannel?.salesChannelDescription ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isAllValid()}
            loading={detail.loading}
            error={detail.error}
            item={salesChannel}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (
                <Stack spacing={2}>
                    {/* Code */}
                    <FormRow label={t('salesChannel.salesChannelCode')}>
                        {!detail.isNew ? (
                            <Typography sx={sx13}>{item.salesChannelCode}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.salesChannelCode}
                                onChange={e => detail.update('salesChannelCode', e.target.value)}
                                error={detail.isEditing && !isSalesChannelCodeValid()}
                            />
                        )}
                    </FormRow>

                    {/* Description */}
                    <FormRow label={t('salesChannel.salesChannelDescription')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.salesChannelDescription}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.salesChannelDescription}
                                onChange={e => detail.update('salesChannelDescription', e.target.value)}
                            />
                        )}
                    </FormRow>

                    {/* Country */}
                    <FormRow label={t('salesChannel.country')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.country?.name}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.country?.id ?? ''}
                                    onChange={e => {
                                        const c = countries.find(c => c.id === e.target.value);
                                        detail.update('country', c ?? null);
                                    }}
                                >
                                    <MenuItem value=""><em>&nbsp;</em></MenuItem>
                                    {[...countries]
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map(c => (
                                            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Pricelists (read-only) */}
                    <FormRow label={t('salesChannel.pricelists')}>
                        <Box>
                            {(item.pricelists ?? []).map(pl => (
                                <Typography sx={sx13} key={pl.id}>
                                    {pl.pricelistCode} {pl.pricelistName}
                                </Typography>
                            ))}
                        </Box>
                    </FormRow>
                </Stack>
            )}
        </DetailViewShell>
    );
}
